import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import PunchCardLogo from "./logos/PunchCardLogo";
import Container from "./Container";
import MaterialIcon from "@/components/ui/MaterialIcon";
import { Text } from "@/components/ui/text";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { User } from "../modules/users/types";
import { workspacesApi } from "@/api/workspaces";

interface HeaderProps {
  user?: User | null;
  onSignOut?: () => void;
  isSigningOut?: boolean;
  embedded?: boolean;
}

const Header: React.FunctionComponent<HeaderProps> = ({
  user,
  onSignOut,
  isSigningOut = false,
  embedded = false,
}) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [messagingActive, setMessagingActive] = useState(user?.workspace?.messagingActive ?? false);
  const [isTogglingMessaging, setIsTogglingMessaging] = useState(false);

  const handleToggleMessaging = async () => {
    const newValue = !messagingActive;
    setMessagingActive(newValue);
    setIsTogglingMessaging(true);
    try {
      await workspacesApi.toggleMessagingActive(newValue);
      toast.success(newValue ? "Messages activated" : "Messages paused");
    } catch {
      setMessagingActive(!newValue);
      toast.error("Failed to update messaging status.");
    } finally {
      setIsTogglingMessaging(false);
    }
  };

  useEffect(() => {
    if (embedded) return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [embedded]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const accountMenuContent = user ? (
    <>
      <div className="mb-3 pl-2">
        <Text variant="sm" className="font-medium text-foreground">
          {user.workspace.name}
        </Text>
        <Text variant="xs" className="text-muted-foreground">
          {user.email}
        </Text>
      </div>

      <div className="space-y-1">
        <button
          onClick={handleToggleMessaging}
          disabled={isTogglingMessaging}
          className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isTogglingMessaging ? (
            <MaterialIcon icon="progress_activity" size={20} className="text-muted-foreground animate-spin" weight={300} opticalSize={20} />
          ) : messagingActive ? (
            <MaterialIcon icon="send" size={20} className="text-success" weight={300} opticalSize={20} />
          ) : (
            <MaterialIcon icon="pause" size={20} className="text-muted-foreground" weight={300} opticalSize={20} />
          )}
          <span className={messagingActive ? undefined : "text-muted-foreground"}>
            {messagingActive ? "Messages active" : "Messages paused"}
          </span>
        </button>
        <Link
          to="/employees"
          className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
        >
          <MaterialIcon icon="group" size={20} className="text-muted-foreground" weight={300} opticalSize={20} />
          Employees
        </Link>
        <Link
          to="/scoring-setup"
          className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
        >
          <MaterialIcon icon="tune" size={20} className="text-muted-foreground" weight={300} opticalSize={20} />
          Scoring Setup
        </Link>
        <Link
          to="/connections"
          className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
        >
          <MaterialIcon icon="automation" size={20} className="text-muted-foreground" weight={300} opticalSize={20} />
          Connections
        </Link>
        <a
          href="https://stripe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
        >
          <MaterialIcon icon="account_balance_wallet" size={20} className="text-muted-foreground" weight={300} opticalSize={20} />
          Billing
        </a>
      </div>

      <div className="my-2 h-px bg-border" />

      <Button
        variant="ghost"
        className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={onSignOut}
        disabled={isSigningOut}
      >
        <MaterialIcon icon="logout" size={20} weight={300} opticalSize={20} />
        {isSigningOut ? "Signing out..." : "Log out"}
      </Button>
    </>
  ) : null;

  return (
    <header
      className={cn(
        "py-4 border-t-4 border-t-highlight",
        embedded
          ? "border-b border-b-border bg-background"
          : "fixed top-0 left-0 right-0 z-50 border-b",
        !embedded && isScrolled
          ? "bg-background/90 backdrop-blur-md border-b-border shadow-sm transition-[background-color,border-color,box-shadow] duration-300"
          : !embedded
            ? "bg-gradient-to-b from-background/70 to-transparent border-b-transparent"
            : ""
      )}
    >
      <Container className="max-w-none flex items-center justify-between">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <PunchCardLogo />
        </Link>

      {user ? (
        <>
          {/* Desktop navigation */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuLink
                  asChild
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isActive("/") && "bg-accent text-accent-foreground"
                  )}
                >
                  <Link to="/">Home</Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink
                  asChild
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isActive("/onboarding") && "bg-accent text-accent-foreground"
                  )}
                >
                  <Link to="/onboarding">Onboarding</Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className={cn(navigationMenuTriggerStyle(), "cursor-pointer")}>
                      Account
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-56 p-3">
                    {accountMenuContent}
                  </PopoverContent>
                </Popover>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Mobile navigation */}
          <div className="md:hidden">
            <Popover open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <MaterialIcon icon="menu" size={24} weight={300} opticalSize={24} />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-3">
                <div className="space-y-1">
                  <Link
                    to="/"
                    className={cn(
                      "flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors",
                      isActive("/") && "bg-accent text-accent-foreground"
                    )}
                  >
                    <MaterialIcon icon="home" size={20} className="text-muted-foreground" weight={300} opticalSize={20} />
                    Home
                  </Link>
                  <Link
                    to="/onboarding"
                    className={cn(
                      "flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors",
                      isActive("/onboarding") && "bg-accent text-accent-foreground"
                    )}
                  >
                    <MaterialIcon icon="school" size={20} className="text-muted-foreground" weight={300} opticalSize={20} />
                    Onboarding
                  </Link>
                </div>

                <div className="my-2 h-px bg-border" />

                {accountMenuContent}
              </PopoverContent>
            </Popover>
          </div>
        </>
      ) : (
        <Button asChild variant="outline">
          <Link to="/sign-in">Sign In</Link>
        </Button>
      )}
      </Container>
    </header>
  );
};

export default Header;
